// 📌 `EstadoCuenta`, `EstadoDisponibilidad` y `EstadoVerificacion` ya NO se importan acá:
// los tres diccionarios que indexaban se retiraron el 2026-09-01 (ver el bloque de abajo).
// Los tipos siguen existiendo en `dominio/contratos.ts` porque los usan las interfaces mock
// (`Conductor`, `UnidadEnVivo`); no son parte de este cambio.
// ⚠️ `dominio/ejemplos.ts` figuraba acá y se RETIRÓ el 2026-09-05 (571 líneas, cero
//    importadores). No buscarlo: no existe. Ver el bloque de abajo.
import type {
  TipoVehiculo,
  EstadoIncidente,
  EstadoRevisionDocumento,
  EstadoSuscripcion,
  EstadoViaje,
  ResultadoOferta,
  TipoDocumento,
  TipoIncidente,
} from '../dominio/contratos';

/**
 * El tono con el que se pinta un estado. No es color por gusto: dice si el
 * operador tiene algo que hacer con esa fila.
 *  bueno    — todo en orden, no requiere atencion
 *  curso    — pasando ahora, se mira pero no se toca
 *  atencion — hay que hacer algo, y no es urgente
 *  malo     — hay que hacer algo ya, o algo fallo
 *  neutro   — sin carga; ni bueno ni malo
 */
export type Tono = 'bueno' | 'curso' | 'atencion' | 'malo' | 'neutro';

/**
 * Los estados se muestran en castellano, como los nombra una persona.
 * El valor tecnico (`SIN_CONDUCTOR`) es del sistema; el operador lee
 * "Nadie lo tomó".
 */
const VIAJE: Record<EstadoViaje, [string, Tono]> = {
  SOLICITADO: ['Solicitado', 'curso'],
  BUSCANDO: ['Buscando conductor', 'atencion'],
  ASIGNADO: ['Asignado', 'curso'],
  EN_ORIGEN: ['En el punto de recogida', 'curso'],
  EN_CURSO: ['En curso', 'curso'],
  FINALIZADO: ['Finalizado', 'bueno'],
  CALIFICADO: ['Calificado', 'bueno'],
  CANCELADO: ['Cancelado', 'malo'],
  SIN_CONDUCTOR: ['Nadie lo tomó', 'malo'],
};

const SUSCRIPCION: Record<EstadoSuscripcion, [string, Tono]> = {
  SIN_INICIAR: ['Sin iniciar', 'neutro'],
  PENDIENTE: ['Esperando el cobro', 'atencion'],
  ACTIVA: ['Activa', 'bueno'],
  FALLIDA: ['Cobro rechazado', 'malo'],
  VENCIDA: ['Vencida', 'malo'],
  CANCELADA: ['Cancelada', 'neutro'],
};

// ═══════════════════════════════════════════════════════════════════════════════════════
// 🔴 ACÁ VIVÍAN `VERIFICACION`, `DISPONIBILIDAD` y `CUENTA`. **RETIRADOS el 2026-09-01
//    porque estaban INDEXADOS POR VALORES QUE EL BACKEND NO PRODUCE** (medido contra
//    `apps/api/prisma/schema.prisma`, no asumido):
//
//      diccionario     tenía                    el enum real es
//      verificacion    SIN_ENVIAR · OBSERVADO   PENDIENTE · SUSPENDIDO (EstadoVerificacionConductor)
//      disponibilidad  FUERA_DE_LINEA           DESCONECTADO           (EstadoDisponibilidad)
//      cuenta          DE_BAJA                  DADA_DE_BAJA           (EstadoCuenta)
//
// El modo de falla era SILENCIOSO, y por eso no bastaba con dejarlos: `leer()` termina en
// `?? [valor, 'neutro']`, así que `<Etiqueta tipo="cuenta" valor={c.estadoCuenta} />` con
// datos reales **compilaba, no avisaba y pintaba `DADA_DE_BAJA` crudo en gris**. En pantalla
// eso se ve como una etiqueta más, no como un error.
//
// ✅ **Los reemplazos YA EXISTÍAN antes de este cambio: `componentes/EtiquetaConductor.tsx`**,
//    indexado por los enums REALES de `dominio/conductoresApi.ts`. `Conductores.tsx` y
//    `Suscripciones.tsx` —las dos únicas pantallas que muestran estos tres campos— ya lo usan.
//
// 🔑 **POR QUÉ RETIRARLOS Y NO CORREGIRLOS, que era la opción preferida a primera vista.**
//    Dos razones medidas, no de estilo:
//      1. Corregir las CLAVES obliga a corregir los TIPOS que las indexan
//         (`EstadoVerificacion` / `EstadoDisponibilidad` / `EstadoCuenta` de
//         `dominio/contratos.ts`) — y **sí hay consumidores de las claves viejas**:
//         `dominio/ejemplos.ts` usaba `FUERA_DE_LINEA` (×3) y `OBSERVADO` (×1). O sea, la
//         condición bajo la cual corregir sería mejor **no se cumple**.
//         ⚠️ Ese archivo se retiró el 2026-09-05, pero **la conclusión NO cambia**: medido
//         ese día, otros 9 archivos siguen usando esas claves. `ejemplos.ts` era UN
//         consumidor citado, nunca el único — no leer su retiro como que esto se destrabó.
//      2. Aun corrigiéndolos, el resultado serían DOS diccionarios idénticos a
//         `EtiquetaConductor` sin ninguna regla que diga cuál usar — que es peor que uno.
//
// 🔑 **Y POR QUÉ SE BORRAN EN VEZ DE MARCARSE CON UN COMENTARIO.** Un comentario no impide
//    escribir `tipo="cuenta"`. Al sacar las tres entradas del mapa `DICCIONARIOS` de abajo,
//    el tipo `Diccionario` se estrecha y **`tsc` rechaza esa llamada**: el error pasa de
//    silencioso a imposible por construcción, que es el mismo estándar que se le exige al
//    selector de costing del ruteo. La marca queda acá, en el lugar donde alguien iría a
//    buscar el diccionario que falta, no en un archivo vecino.
//
// ⚠️ NO se tocaron los otros cinco diccionarios. `suscripcion` y `oferta` **sí coinciden**
//    valor por valor con el backend (verificado); `incidente`, `revision` y `TIPO_DOCUMENTO`
//    también difieren, pero eso es un hallazgo aparte, con sus propios reemplazos ya en
//    `Verificacion.tsx` / `Incidentes.tsx` / `Panel.tsx`, y ampliarlo acá sería mezclar dos
//    cambios en un mismo PR.
// ═══════════════════════════════════════════════════════════════════════════════════════

const INCIDENTE: Record<EstadoIncidente, [string, Tono]> = {
  ABIERTO: ['Abierto', 'malo'],
  EN_ATENCION: ['En atención', 'atencion'],
  RESUELTO: ['Resuelto', 'bueno'],
  CERRADO: ['Cerrado', 'neutro'],
};

const REVISION: Record<EstadoRevisionDocumento, [string, Tono]> = {
  PENDIENTE: ['Pendiente', 'atencion'],
  APROBADO: ['Aprobado', 'bueno'],
  OBSERVADO: ['Observado', 'atencion'],
  RECHAZADO: ['Rechazado', 'malo'],
};

const OFERTA: Record<ResultadoOferta, [string, Tono]> = {
  PENDIENTE: ['Vigente', 'curso'],
  GANADA: ['Ganada', 'bueno'],
  PERDIDA: ['Perdida', 'neutro'],
  RECHAZADA: ['Rechazada', 'atencion'],
  EXPIRADA: ['Expirada', 'atencion'],
};

export const TIPO_INCIDENTE: Record<TipoIncidente, string> = {
  SOS: 'SOS',
  DISPUTA: 'Disputa',
  RECLAMO: 'Reclamo',
  ACCIDENTE: 'Accidente',
  OTRO: 'Otro',
};

export const TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
  DNI: 'DNI',
  LICENCIA: 'Licencia de conducir',
  SOAT: 'SOAT',
  TARJETA_PROPIEDAD: 'Tarjeta de propiedad',
  REVISION_TECNICA: 'Revisión técnica',
  ANTECEDENTES: 'Antecedentes',
};

/**
 * 📌 `MOTOTAXI: 'Mototaxi'` se retiró el 2026-09-01 con la clase (decisión del owner: el
 * servicio es sólo vehículos taxi). El `Record<TipoVehiculo, string>` es exhaustivo, así que
 * mientras `TipoVehiculo` no tenga ese valor `tsc` rechaza volver a escribir la clave.
 */
export const TIPO_VEHICULO: Record<TipoVehiculo, string> = {
  AUTO: 'Auto',
};

// ⚠️ `verificacion`, `disponibilidad` y `cuenta` **no están acá a propósito** — ver el bloque
// de retiro más arriba. Sacarlas del mapa es lo que estrecha el tipo `Diccionario` y convierte
// `<Etiqueta tipo="cuenta" …>` en un error de compilación en vez de una etiqueta gris.
// Para esos tres campos: `componentes/EtiquetaConductor.tsx`.
const DICCIONARIOS = {
  viaje: VIAJE,
  suscripcion: SUSCRIPCION,
  incidente: INCIDENTE,
  revision: REVISION,
  oferta: OFERTA,
} as const;

type Diccionario = keyof typeof DICCIONARIOS;

function leer(tipo: Diccionario, valor: string): [string, Tono] {
  const tabla = DICCIONARIOS[tipo] as Record<string, [string, Tono] | undefined>;
  return tabla[valor] ?? [valor, 'neutro'];
}

/** El tono de un estado, para pintar la cinta del canto de la fila. */
export const tonoDe = (tipo: Diccionario, valor: string): Tono => leer(tipo, valor)[1];

export function Etiqueta({ tipo, valor }: { tipo: Diccionario; valor: string }) {
  const [texto, tono] = leer(tipo, valor);
  return <span className={`etiqueta etiqueta--${tono}`}>{texto}</span>;
}
